import apiClient from '@/lib/api-client';
import type { User } from '@/types/auth';
import type {
  ProfileUpdateRequest,
  EmailChangeRequest,
  EmailChangeVerification,
  PasswordResetRequest,
  PasswordResetVerification,
  PasswordResetValidation
} from '@/types/profile';

class ProfileService {
  /**
   * Update user profile (first name, last name, phone)
   */
  async updateProfile(data: ProfileUpdateRequest): Promise<User> {
    const { data: response } = await apiClient.put<{ data: any }>('/v1/auth/profile', {
      first_name: data.firstName,
      last_name: data.lastName,
      phone: data.phone
    });

    return this.transformUser(response.data);
  }

  /**
   * Request email change - sends verification code to new email
   */
  async requestEmailChange(data: EmailChangeRequest): Promise<{ message: string }> {
    const { data: response } = await apiClient.post<{ data: any }>('/v1/auth/profile/request_email_change', {
      new_email: data.newEmail
    });

    return { message: response.data.message };
  }

  /**
   * Verify email change with 6-digit code
   */
  async verifyEmailChange(data: EmailChangeVerification): Promise<User> {
    const { data: response } = await apiClient.post<{ data: any }>('/v1/auth/profile/verify_email_change', {
      verification_code: data.verificationCode
    });

    return this.transformUser(response.data.user);
  }

  /**
   * Request password reset - sends email with reset link
   */
  async requestPasswordReset(data: PasswordResetRequest): Promise<{ message: string }> {
    const { data: response } = await apiClient.post<{ data: any }>('/v1/auth/passwords/forgot', {
      email: data.email
    });

    return { message: response.data.message };
  }

  /**
   * Validate password reset token
   */
  async validateResetToken(token: string): Promise<PasswordResetValidation> {
    try {
      const { data: response } = await apiClient.get<{ data: any }>(`/v1/auth/passwords/validate_token/${token}`);
      return {
        valid: response.data.valid,
        message: response.data.message
      };
    } catch (error: any) {
      return {
        valid: false,
        message: error.response?.data?.message || 'Invalid or expired token'
      };
    }
  }

  /**
   * Reset password using token from email
   */
  async resetPassword(data: PasswordResetVerification): Promise<{ message: string }> {
    const { data: response } = await apiClient.post<{ data: any }>('/v1/auth/passwords/reset', {
      token: data.token,
      new_password: data.newPassword,
      password_confirmation: data.passwordConfirmation
    });

    return { message: response.data.message };
  }

  /**
   * Transform backend user response to frontend User type
   */
  private transformUser(apiUser: any): User {
    return {
      id: apiUser.id,
      email: apiUser.email,
      firstName: apiUser.first_name,
      lastName: apiUser.last_name,
      phone: apiUser.phone,
      role: apiUser.role,
      organizationId: apiUser.organization_id,
      active: apiUser.active,
      pendingEmail: apiUser.pending_email,
      emailConfirmed: apiUser.email_confirmed ?? true,
      emailConfirmedAt: apiUser.email_confirmed_at,
      createdAt: apiUser.created_at,
      updatedAt: apiUser.updated_at
    };
  }
}

export const profileService = new ProfileService();
